import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Search, Save, Plus, Trash2, Send, Mail, KeyRound, ShieldCheck, Bell, BellOff, BellRing, Printer, Volume2 } from 'lucide-react';
import { BaseMap, Marker, RESTAURANT_ICON, Recenter } from '@/components/MapBase';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog';
import { useAuthStore } from '@/lib/auth';
import { DEFAULT_CATEGORY_LABELS, mergeCategoryLabels } from '@/lib/categoryLabels';
import {
  isSoundEnabled, setSoundEnabled,
  isBrowserNotifEnabled, setBrowserNotifEnabled,
  browserNotifSupported, browserNotifPermission, requestBrowserNotifPermission,
  playOrderChime,
} from '@/lib/notifications';

const DAY_LABELS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

function defaultHours() {
  return Array.from({ length: 7 }).map((_, i) => ({
    day: i,
    open: i !== 0, // closed Monday
    slots: [{ from: '11:30', to: '14:30' }, { from: '18:30', to: '22:30' }],
  }));
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [addr, setAddr] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const user = useAuthStore(s => s.user);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [notifOn, setNotifOn] = useState(isBrowserNotifEnabled());
  const [notifPerm, setNotifPerm] = useState(browserNotifPermission());

  useEffect(() => {
    api.get('/settings').then(r => {
      const s = r.data;
      if (!s.opening_hours || !s.opening_hours.length) s.opening_hours = defaultHours();
      setSettings(s);
    });
    api.get('/admin/email/status').then(r => setEmailStatus(r.data)).catch(() => {});
  }, []);

  const geocode = async () => {
    if (!addr) return;
    try {
      const r = await api.post('/quote', { address: addr });
      setSettings(s => ({ ...s, lat: r.data.lat, lng: r.data.lng, address: r.data.display_name || addr }));
      toast.success('Coordinate aggiornate');
    } catch (e) { toast.error('Indirizzo non trovato'); }
  };

  const save = async () => {
    try {
      await api.patch('/admin/settings', settings);
      toast.success('Impostazioni salvate');
    } catch { toast.error('Errore salvataggio'); }
  };

  const [hoursField, setHoursField] = useState('opening_hours');

  const currentHours = () => settings?.[hoursField] || settings?.opening_hours || [];

  const updateDay = (idx, patch) => {
    setSettings(s => ({
      ...s,
      [hoursField]: (s[hoursField] || s.opening_hours || []).map((d, i) => i === idx ? { ...d, ...patch } : d),
    }));
  };

  const updateSlot = (dayIdx, slotIdx, patch) => {
    setSettings(s => ({
      ...s,
      [hoursField]: (s[hoursField] || s.opening_hours || []).map((d, i) => {
        if (i !== dayIdx) return d;
        return { ...d, slots: d.slots.map((sl, j) => j === slotIdx ? { ...sl, ...patch } : sl) };
      }),
    }));
  };

  const addSlot = (dayIdx) => {
    setSettings(s => ({
      ...s,
      [hoursField]: (s[hoursField] || s.opening_hours || []).map((d, i) => {
        if (i !== dayIdx) return d;
        return { ...d, slots: [...d.slots, { from: '18:30', to: '22:30' }] };
      }),
    }));
  };

  const removeSlot = (dayIdx, slotIdx) => {
    setSettings(s => ({
      ...s,
      [hoursField]: (s[hoursField] || s.opening_hours || []).map((d, i) => {
        if (i !== dayIdx) return d;
        return { ...d, slots: d.slots.filter((_, j) => j !== slotIdx) };
      }),
    }));
  };

  const copyFromGlobal = () => {
    if (hoursField === 'opening_hours') return;
    setSettings(s => ({ ...s, [hoursField]: JSON.parse(JSON.stringify(s.opening_hours || [])) }));
    toast.success('Orari copiati dai generali');
  };

  const resetChannelHours = () => {
    if (hoursField === 'opening_hours') return;
    setSettings(s => {
      const cp = { ...s };
      cp[hoursField] = null;
      return cp;
    });
    toast.info('Il canale userà gli orari generali');
  };

  const sendTestEmail = async () => {
    if (!testEmail) return;
    try {
      await api.post(`/admin/email/test?to=${encodeURIComponent(testEmail)}`);
      toast.success(`Email di test inviata a ${testEmail}`);
      setTestEmailOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Invio fallito'); }
  };

  if (!settings) return <AdminLayout>Caricamento...</AdminLayout>;

  return (
    <AdminLayout testid="admin-settings-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Impostazioni</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Dati del ristorante</h1>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="bg-[--brand-surface-2] p-1 rounded-xl flex flex-wrap gap-1 h-auto justify-start">
          <TabsTrigger value="general" className="rounded-lg">Anagrafica</TabsTrigger>
          <TabsTrigger value="channels" className="rounded-lg" data-testid="admin-settings-tab-channels">Canali d&apos;ordine</TabsTrigger>
          <TabsTrigger value="hours" className="rounded-lg">Orari di apertura</TabsTrigger>
          <TabsTrigger value="email" className="rounded-lg">Email</TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-lg">Notifiche</TabsTrigger>
          <TabsTrigger value="labels" className="rounded-lg">Etichette</TabsTrigger>
          <TabsTrigger value="printer" className="rounded-lg">Stampante</TabsTrigger>
          <TabsTrigger value="security" className="rounded-lg">Sicurezza</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl bg-white border border-[--brand-border] p-5 space-y-3">
              <div><Label>Nome ristorante</Label><Input value={settings.name} onChange={e => setSettings({...settings, name: e.target.value})} className="rounded-xl"/></div>
              <div><Label>Telefono</Label><Input value={settings.phone || ''} onChange={e => setSettings({...settings, phone: e.target.value})} className="rounded-xl"/></div>
              <div><Label>Indirizzo</Label><Input value={settings.address || ''} onChange={e => setSettings({...settings, address: e.target.value})} className="rounded-xl"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Latitudine</Label><Input type="number" step="0.000001" value={settings.lat} onChange={e => setSettings({...settings, lat: parseFloat(e.target.value) || 0})} className="rounded-xl"/></div>
                <div><Label>Longitudine</Label><Input type="number" step="0.000001" value={settings.lng} onChange={e => setSettings({...settings, lng: parseFloat(e.target.value) || 0})} className="rounded-xl"/></div>
              </div>
              <div>
                <Label>Cerca dall'indirizzo</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={addr} onChange={e => setAddr(e.target.value)} placeholder="Es. Piazza del Duomo Milano" className="rounded-xl"/>
                  <Button onClick={geocode} variant="secondary" className="rounded-xl"><Search size={14}/></Button>
                </div>
              </div>
              <Button onClick={save} className="w-full rounded-xl bg-[--brand-primary]" data-testid="admin-settings-save-button"><Save size={14} className="mr-1"/>Salva</Button>
            </div>

            <div className="h-[400px]">
              <BaseMap center={[settings.lat, settings.lng]} zoom={14} style={{ height: '100%', minHeight: 400 }}>
                <Recenter position={[settings.lat, settings.lng]} />
                <Marker position={[settings.lat, settings.lng]} icon={RESTAURANT_ICON} />
              </BaseMap>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="channels" className="mt-4" data-testid="admin-settings-channels-tab">
          <div className="rounded-2xl bg-white border border-[--brand-border] p-6 space-y-4">
            <div>
              <h3 className="font-display font-bold text-xl">Canali d&apos;ordine</h3>
              <p className="text-sm text-[--brand-muted] mt-1">
                Attiva o disattiva la <b>consegna a domicilio</b> e l&apos;<b>asporto</b>.
                Quando un canale è spento, i clienti non possono selezionarlo al checkout
                e gli ordini di quel tipo vengono rifiutati anche via API.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-xl bg-[--brand-surface-2] p-4 gap-4">
              <div className="min-w-0">
                <div className="font-semibold flex items-center gap-2">
                  🛵 Consegna a domicilio
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${settings.delivery_enabled !== false ? 'bg-[--brand-primary]/15 text-[--brand-primary]' : 'bg-[--brand-danger]/15 text-[--brand-danger]'}`}>
                    {settings.delivery_enabled !== false ? 'Attiva' : 'Disattivata'}
                  </span>
                </div>
                <div className="text-xs text-[--brand-muted] mt-1">
                  I clienti possono ordinare a domicilio nelle zone configurate.
                </div>
              </div>
              <Switch
                checked={settings.delivery_enabled !== false}
                onCheckedChange={(v) => setSettings(s => ({ ...s, delivery_enabled: v }))}
                data-testid="admin-delivery-enabled-switch"
              />
            </div>

            <div className="flex items-center justify-between rounded-xl bg-[--brand-surface-2] p-4 gap-4">
              <div className="min-w-0">
                <div className="font-semibold flex items-center gap-2">
                  🏪 Asporto (Take-away)
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${settings.takeaway_enabled !== false ? 'bg-[--brand-primary]/15 text-[--brand-primary]' : 'bg-[--brand-danger]/15 text-[--brand-danger]'}`}>
                    {settings.takeaway_enabled !== false ? 'Attivo' : 'Disattivato'}
                  </span>
                </div>
                <div className="text-xs text-[--brand-muted] mt-1">
                  I clienti possono ordinare e ritirare qui, senza costo di consegna.
                </div>
              </div>
              <Switch
                checked={settings.takeaway_enabled !== false}
                onCheckedChange={(v) => setSettings(s => ({ ...s, takeaway_enabled: v }))}
                data-testid="admin-takeaway-enabled-switch"
              />
            </div>

            {(settings.delivery_enabled === false && settings.takeaway_enabled === false) && (
              <div className="rounded-xl bg-[#FDECEC] border border-[--brand-danger]/30 text-[--brand-danger] p-4 text-sm">
                ⚠️ <b>Entrambi i canali sono disattivati.</b> I clienti non potranno effettuare nuovi ordini
                finché non ne riattivi almeno uno.
              </div>
            )}

            <Button onClick={save} className="w-full rounded-xl bg-[--brand-primary]" data-testid="admin-channels-save-button">
              <Save size={14} className="mr-1"/>Salva canali
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="hours" className="mt-4">
          <div className="rounded-2xl bg-white border border-[--brand-border] p-5 space-y-4" data-testid="admin-hours-editor">
            {/* Channel selector: general / delivery / takeaway */}
            <div className="flex flex-wrap gap-2 items-center" data-testid="admin-hours-channel-selector">
              <button type="button" onClick={() => setHoursField('opening_hours')}
                data-testid="hours-channel-general"
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${hoursField==='opening_hours' ? 'bg-[--brand-primary] text-white' : 'bg-[--brand-surface-2]'}`}>
                🕐 Orari generali
              </button>
              <button type="button" onClick={() => setHoursField('delivery_hours')}
                data-testid="hours-channel-delivery"
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${hoursField==='delivery_hours' ? 'bg-[--brand-primary] text-white' : 'bg-[--brand-surface-2]'}`}>
                🛵 Consegna
              </button>
              <button type="button" onClick={() => setHoursField('takeaway_hours')}
                data-testid="hours-channel-takeaway"
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${hoursField==='takeaway_hours' ? 'bg-[--brand-primary] text-white' : 'bg-[--brand-surface-2]'}`}>
                🏪 Asporto
              </button>
              {hoursField !== 'opening_hours' && (
                <div className="ml-auto flex gap-2 flex-wrap">
                  <Button size="sm" variant="secondary" onClick={copyFromGlobal} data-testid="hours-copy-from-global">Copia dai generali</Button>
                  <Button size="sm" variant="ghost" onClick={resetChannelHours} data-testid="hours-reset-channel">Usa gli orari generali</Button>
                </div>
              )}
            </div>
            <div className="text-sm text-[--brand-muted]">
              {hoursField === 'opening_hours'
                ? 'Fasce di apertura generali. Se non specifichi orari diversi per Consegna/Asporto, questi vengono usati per entrambi.'
                : hoursField === 'delivery_hours'
                  ? 'Orari personalizzati per la Consegna a domicilio. Se non ne imposti, usa gli orari generali.'
                  : 'Orari personalizzati per l\'Asporto. Se non ne imposti, usa gli orari generali.'}
            </div>
            {(!settings[hoursField] || settings[hoursField].length === 0) && hoursField !== 'opening_hours' && (
              <div className="rounded-lg bg-[#FFF6E1] border border-[#F4D89A] p-3 text-xs">
                Questo canale sta usando gli <b>orari generali</b>. Clicca <b>&quot;Copia dai generali&quot;</b> per iniziare a personalizzarli.
              </div>
            )}
            {(currentHours()).map((d, dayIdx) => (
              <div key={dayIdx} className="rounded-xl border border-[--brand-border] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-bold">{DAY_LABELS[dayIdx]}</div>
                  <div className="flex items-center gap-2">
                    <Switch checked={d.open} onCheckedChange={v => updateDay(dayIdx, { open: v })} data-testid={`admin-hours-day-${dayIdx}-open`}/>
                    <span className="text-sm">{d.open ? 'Aperto' : 'Chiuso'}</span>
                  </div>
                </div>
                {d.open && (
                  <div className="space-y-2">
                    {d.slots.map((s, slotIdx) => (
                      <div key={slotIdx} className="flex items-center gap-2">
                        <span className="text-xs text-[--brand-muted]">Dalle</span>
                        <Input type="time" value={s.from} onChange={e => updateSlot(dayIdx, slotIdx, { from: e.target.value })} className="rounded-lg w-28"/>
                        <span className="text-xs text-[--brand-muted]">alle</span>
                        <Input type="time" value={s.to} onChange={e => updateSlot(dayIdx, slotIdx, { to: e.target.value })} className="rounded-lg w-28"/>
                        <Button size="sm" variant="ghost" onClick={() => removeSlot(dayIdx, slotIdx)}><Trash2 size={14} className="text-[--brand-danger]"/></Button>
                      </div>
                    ))}
                    <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => addSlot(dayIdx)}><Plus size={12} className="mr-1"/>Aggiungi fascia</Button>
                  </div>
                )}
              </div>
            ))}
            <Button onClick={save} className="w-full rounded-xl bg-[--brand-primary]" data-testid="admin-hours-save-button"><Save size={14} className="mr-1"/>Salva orari</Button>
          </div>
        </TabsContent>

        <TabsContent value="email" className="mt-4">
          <div className="rounded-2xl bg-white border border-[--brand-border] p-5 space-y-4">
            <div>
              <h3 className="font-display font-bold flex items-center gap-2"><Mail size={16}/> Notifiche email</h3>
              <p className="text-sm text-[--brand-muted] mt-1">Le email vengono inviate via SMTP Gmail configurato nel file <code>backend/.env</code>.</p>
            </div>
            <div className="rounded-xl bg-[--brand-surface-2] p-3 text-sm space-y-1">
              <div>Stato SMTP: <b className={emailStatus?.configured ? 'text-[--brand-success]' : 'text-[--brand-danger]'}>{emailStatus?.configured ? 'Configurato' : 'Non configurato'}</b></div>
              <div>Host: <span className="tabular-nums">{emailStatus?.smtp_host || '—'}</span></div>
              <div>Utente mittente: <span className="tabular-nums">{emailStatus?.smtp_user || '—'}</span></div>
              <div>Nome mittente: {emailStatus?.smtp_from_name || '—'}</div>
              <div>Email admin (notifiche nuovi ordini): {emailStatus?.admin_notification_email || <span className="italic text-[--brand-muted]">non impostata</span>}</div>
            </div>
            {!emailStatus?.configured && (
              <div className="rounded-xl bg-[#FFF6E1] border border-[#F4D89A] px-4 py-3 text-sm">
                Per attivare le email, aggiungi al file <code>backend/.env</code>:<br/>
                <code>SMTP_USER=tuoindirizzo@gmail.com</code><br/>
                <code>SMTP_PASSWORD=app-password-16-caratteri</code><br/>
                <code>ADMIN_NOTIFICATION_EMAIL=tuoindirizzo@gmail.com</code><br/>
                poi riavvia il backend.
              </div>
            )}
            <Button onClick={() => setTestEmailOpen(true)} disabled={!emailStatus?.configured} className="rounded-xl bg-[--brand-primary]" data-testid="admin-email-test-button">
              <Send size={14} className="mr-1"/>Invia email di test
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <div className="rounded-2xl bg-white border border-[--brand-border] p-5 space-y-4 max-w-2xl">
            <div>
              <h3 className="font-display font-bold flex items-center gap-2"><Bell size={16}/> Notifiche nuovi ordini</h3>
              <p className="text-sm text-[--brand-muted] mt-1">Ricevi un avviso sonoro e/o visivo quando arriva un nuovo ordine.</p>
            </div>

            <div className="rounded-xl border border-[--brand-border] p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="font-semibold flex items-center gap-2"><Volume2 size={14}/> Suono di notifica</div>
                <div className="text-xs text-[--brand-muted] mt-1">Riproduce un breve "ding" ogni volta che arriva un nuovo ordine.</div>
              </div>
              <Switch checked={soundOn} onCheckedChange={(v) => { setSoundOn(v); setSoundEnabled(v); if (v) playOrderChime(); }} data-testid="admin-notif-sound-switch" />
            </div>

            <div className="rounded-xl border border-[--brand-border] p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="font-semibold flex items-center gap-2"><BellRing size={14}/> Notifiche browser (system)</div>
                <div className="text-xs text-[--brand-muted] mt-1">
                  Notifica pop-up sul desktop/tablet anche quando il browser è in background.
                  {notifPerm === 'granted' && <span className="text-[--brand-success] ml-1 font-semibold">✓ Permesso concesso</span>}
                  {notifPerm === 'denied' && <span className="text-[--brand-danger] ml-1 font-semibold">✗ Bloccato dal browser</span>}
                  {notifPerm === 'default' && <span className="text-[--brand-warning] ml-1 font-semibold">⚠ Permesso non richiesto</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notifPerm !== 'granted' && browserNotifSupported() && (
                  <Button size="sm" variant="secondary" onClick={async () => {
                    const p = await requestBrowserNotifPermission();
                    setNotifPerm(p);
                    if (p === 'granted') toast.success('Notifiche browser abilitate');
                    else if (p === 'denied') toast.error('Permesso negato — modifica dalle impostazioni del browser');
                  }} data-testid="admin-notif-request-perm">
                    Attiva
                  </Button>
                )}
                <Switch checked={notifOn} onCheckedChange={(v) => { setNotifOn(v); setBrowserNotifEnabled(v); }} data-testid="admin-notif-browser-switch" />
              </div>
            </div>

            <div>
              <Button variant="secondary" onClick={() => playOrderChime({ volume: 0.6 })} className="rounded-xl" data-testid="admin-notif-test-sound">
                <Volume2 size={14} className="mr-2"/>Prova il suono
              </Button>
            </div>

            <div className="rounded-xl bg-[#FFF6E1] border border-[#F4D89A] px-4 py-3 text-xs">
              <b>💡 Consigli</b>
              <ul className="mt-1 space-y-0.5 list-disc list-inside">
                <li>Su tablet Android, apri il pannello admin in una scheda dedicata di Chrome e mantieni lo schermo acceso durante il servizio.</li>
                <li>Installando l'app come PWA (icona su home) le notifiche funzionano anche a schermo bloccato.</li>
                <li>Il suono viene riprodotto solo dopo la prima interazione con la pagina (regola dei browser).</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="labels" className="mt-4">
          <div className="rounded-2xl bg-white border border-[--brand-border] p-5 space-y-4 max-w-2xl">
            <div>
              <h3 className="font-display font-bold">Etichette delle categorie</h3>
              <p className="text-sm text-[--brand-muted] mt-1">Personalizza il nome mostrato ai clienti per ogni categoria di ingredienti nel Builder e nel menu.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {Object.entries(DEFAULT_CATEGORY_LABELS).map(([key, def]) => (
                <div key={key}>
                  <Label className="capitalize text-xs">{key} <span className="text-[--brand-muted]">(default: {def})</span></Label>
                  <Input
                    value={(settings.category_labels && settings.category_labels[key]) || ''}
                    placeholder={def}
                    onChange={e => setSettings(s => ({
                      ...s,
                      category_labels: { ...(s.category_labels || {}), [key]: e.target.value }
                    }))}
                    className="rounded-xl"
                    data-testid={`admin-label-${key}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={save} className="rounded-xl bg-[--brand-primary]" data-testid="admin-labels-save"><Save size={14} className="mr-2"/>Salva etichette</Button>
              <Button variant="secondary" onClick={() => setSettings(s => ({ ...s, category_labels: null }))} className="rounded-xl">Ripristina default</Button>
            </div>
            <div className="rounded-xl bg-[#FFF6E1] border border-[#F4D89A] px-4 py-3 text-xs">
              <b>💡 Esempi:</b> Puoi cambiare "Proteine" in "Pesce e carne", "Topping" in "Verdure", "Salse" in "Condimenti", ecc.
            </div>
          </div>
        </TabsContent>

        <TabsContent value="printer" className="mt-4">
          <div className="rounded-2xl bg-white border border-[--brand-border] p-5 space-y-4 max-w-2xl">
            <div>
              <h3 className="font-display font-bold flex items-center gap-2"><Printer size={16}/> Stampante di rete (LAN)</h3>
              <p className="text-sm text-[--brand-muted] mt-1">
                Configura la stampante termica del tuo registratore di cassa RCH. Vedi la <a className="text-[--brand-primary] underline" href="#printer-guide">guida in fondo</a>.
              </p>
            </div>

            <div className="rounded-xl border border-[--brand-border] p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="font-semibold">Stampante abilitata</div>
                <div className="text-xs text-[--brand-muted] mt-1">Mostra il pulsante "Stampa comanda" negli ordini.</div>
              </div>
              <Switch checked={!!settings.printer_enabled} onCheckedChange={(v) => setSettings(s => ({ ...s, printer_enabled: v }))} data-testid="admin-printer-enabled-switch" />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Indirizzo IP</Label>
                <Input value={settings.printer_ip || ''} onChange={e => setSettings(s => ({ ...s, printer_ip: e.target.value }))} placeholder="192.168.1.50" className="rounded-xl" data-testid="admin-printer-ip"/>
              </div>
              <div>
                <Label>Porta</Label>
                <Input type="number" value={settings.printer_port ?? 9100} onChange={e => setSettings(s => ({ ...s, printer_port: parseInt(e.target.value) || 9100 }))} placeholder="9100" className="rounded-xl" data-testid="admin-printer-port"/>
              </div>
              <div>
                <Label>Larghezza carta (mm)</Label>
                <Input type="number" value={settings.printer_paper_width ?? 80} onChange={e => setSettings(s => ({ ...s, printer_paper_width: parseInt(e.target.value) || 80 }))} placeholder="80" className="rounded-xl" data-testid="admin-printer-paper"/>
              </div>
            </div>

            <div className="rounded-xl border border-[--brand-border] p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="font-semibold">Stampa automatica alla ricezione</div>
                <div className="text-xs text-[--brand-muted] mt-1">Apre il dialog di stampa (o stampa direttamente in kiosk mode) appena arriva un nuovo ordine.</div>
              </div>
              <Switch checked={!!settings.printer_auto_print} onCheckedChange={(v) => setSettings(s => ({ ...s, printer_auto_print: v }))} data-testid="admin-printer-auto-switch" />
            </div>

            <Button onClick={save} className="rounded-xl bg-[--brand-primary]" data-testid="admin-printer-save">
              <Save size={14} className="mr-2"/>Salva impostazioni stampante
            </Button>

            <div id="printer-guide" className="rounded-xl bg-[#F3FBF7] border border-[--brand-primary]/30 px-4 py-3 text-xs mt-6">
              <div className="font-semibold text-sm mb-2">📖 Guida: come stampare le comande dal tablet Android</div>
              <p className="mb-2"><b>Setup consigliato (una tantum, 5 minuti):</b></p>
              <ol className="space-y-1 list-decimal list-inside">
                <li>Verifica l'<b>IP della stampante RCH</b>: dal menu della stampante o dal registratore di cassa → Impostazioni rete. Salvalo sopra.</li>
                <li>Sul tablet, installa dall'app <b>Google Play</b> l'app gratuita <b>RawBT ESC/POS Print Service</b> (funziona con stampanti Epson/RCH/Star LAN).</li>
                <li>Apri RawBT → <b>Impostazioni connessione</b> → "TCP" → inserisci IP e porta 9100 → Salva.</li>
                <li>Sul tablet: <b>Impostazioni → Stampa → Servizi di stampa → RawBT → ON</b>.</li>
                <li>Torna qui e attiva "Stampa automatica alla ricezione".</li>
                <li>Ora ogni nuovo ordine stamperà da solo la comanda sulla tua stampante. ✅</li>
              </ol>
              <p className="mt-3"><b>In alternativa (senza RawBT):</b> aggiungi la stampante come "stampante di sistema" del tablet (via IP), poi Chrome mostrerà la finestra di stampa standard che dovrai confermare a mano ad ogni ordine.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="security" className="mt-4">
          <div className="rounded-2xl bg-white border border-[--brand-border] p-5 space-y-4 max-w-2xl">
            <div>
              <h3 className="font-display font-bold flex items-center gap-2"><ShieldCheck size={16}/> Sicurezza account</h3>
              <p className="text-sm text-[--brand-muted] mt-1">Gestisci l'accesso al tuo account admin.</p>
            </div>

            <div className="rounded-xl bg-[--brand-surface-2] p-4">
              <div className="text-xs uppercase tracking-widest text-[--brand-muted] mb-1">Email dell'account</div>
              <div className="font-semibold text-[--brand-fg]" data-testid="admin-account-email">{user?.email || '—'}</div>
              <div className="text-xs text-[--brand-muted] mt-1">L'email non è modificabile dal pannello. Per cambiarla contatta il supporto tecnico.</div>
            </div>

            <div className="rounded-xl border border-[--brand-border] p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold flex items-center gap-2"><KeyRound size={14}/> Password</div>
                <div className="text-xs text-[--brand-muted] mt-1">Aggiorna la tua password. Consigliato ogni 3-6 mesi.</div>
              </div>
              <Button onClick={() => setPwdOpen(true)} className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="admin-open-change-password">
                Cambia password
              </Button>
            </div>

            <div className="rounded-xl bg-[#FFF6E1] border border-[#F4D89A] px-4 py-3 text-sm">
              <b>💡 Suggerimenti per una password sicura:</b>
              <ul className="mt-1 space-y-0.5 list-disc list-inside text-xs">
                <li>Almeno 12 caratteri</li>
                <li>Mix di maiuscole, minuscole, numeri e simboli</li>
                <li>Non riutilizzare la stessa password su più siti</li>
                <li>Cambiala se sospetti che qualcuno la conosca</li>
              </ul>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Invia email di test</DialogTitle></DialogHeader>
          <div>
            <Label>Destinatario</Label>
            <Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@example.com" className="rounded-xl" data-testid="admin-email-test-input"/>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setTestEmailOpen(false)}>Annulla</Button>
            <Button onClick={sendTestEmail} className="bg-[--brand-primary]" data-testid="admin-email-test-send"><Send size={14} className="mr-1"/>Invia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </AdminLayout>
  );
}
